import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, ExternalLink, X } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { getListingReviewDetail } from '../../services/providerListingDetailService';
import { transformFacilityData } from '../../utils/listingTransformUtils';
import { getFullListingDescription, getListingDetailsTitle, getListingLinks } from '../../utils/listingDetails';
import { useDialogA11y } from '../../landing/lib/useDialogA11y';
import ListingFront from './ListingFront';
import ListingBack from './ListingBack';
import ExternalListingFront from './ExternalListingFront';
import ExternalListingBack from './ExternalListingBack';
import { TooltipContext } from './TooltipContext';
import './FlippableListingCard.css';
import './ListingInterestReview.css';

const steps = ['Card front', 'Card back', 'Full details'];
const hasDescription = (listing) => Boolean(listing?.fullDescription || listing?.description);

function rawListingOf(listing) {
  return listing?._original?.listing || listing?._original || listing;
}

/** One deliberate review before any provider interest is saved. */
export default function ListingInterestReview({ listing, onCancel, onConfirm }) {
  const authContext = useContext(AuthContext);
  const auth = authContext?.auth;
  const rawListing = rawListingOf(listing);
  const listingId = listing._id || listing.id || rawListing?._id;
  const [detail, setDetail] = useState(hasDescription(rawListing) ? rawListing : null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [step, setStep] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [openId, setOpenId] = useState(null);
  const bodyRef = useRef(null);
  const dialogRef = useDialogA11y({ onClose: onCancel });

  // Inbound interest and notification links may supply only an ID/title.
  // Load their posting before offering any review or interest controls.
  useEffect(() => {
    if (hasDescription(rawListing)) return undefined;
    let active = true;
    setError(null);
    getListingReviewDetail(listingId).then((loaded) => {
      if (!active) return;
      // The map addresses normalizedId; internal hydration returns the
      // backing listing's _id plus that same normalizedId.
      const matchesId = loaded && [loaded._id, loaded.id, loaded.normalizedId]
        .some((id) => id && String(id) === String(listingId));
      if (!matchesId) {
        setError('load');
        return;
      }
      setDetail(loaded);
    }).catch((failure) => {
      if (active) setError(failure?.code === 'LISTING_UNAVAILABLE' ? 'unavailable' : 'load');
    });
    return () => { active = false; };
  }, [listingId, rawListing, attempt]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    if (step > 0) bodyRef.current?.focus();
    setOpenId(null);
  }, [step]);

  const profile = useMemo(() => {
    if (!detail) return null;
    const source = detail.sourceType || detail._listingType || detail.source ||
      (detail.section && !detail.facility ? 'external' : 'internal');
    // Public normalized detail rows already carry the external card's fields.
    // Feeding them through the legacy GasWork transform drops description,
    // title and clinical data from sources that use the normalized shape.
    if (source !== 'internal') return { ...detail, sourceType: source };
    // Anonymous decks already hand us the card's display model.
    if (detail.clinicalExpectations && !detail.facility && detail.compensation) return detail;
    return transformFacilityData(detail, auth?.user?.providerProfile?.yearsOfExperience ?? null);
  }, [detail, auth?.user?.providerProfile?.yearsOfExperience]);
  const external = profile && profile.sourceType && profile.sourceType !== 'internal';
  const title = getListingDetailsTitle(detail || listing);
  const links = getListingLinks(detail || {});
  const expandDetails = () => {
    // External card fronts also offer a description link. It must still
    // lead through the back before opening the complete posting.
    setStep((current) => Math.min(current + 1, 2));
  };

  return createPortal(
    <div className="listing-interest-review__backdrop" onClick={(event) => {
      event.stopPropagation();
      if (event.target === event.currentTarget) onCancel();
    }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="listing-interest-review-title"
        aria-describedby="listing-interest-review-help" tabIndex={-1} className="listing-interest-review">
        <header className="listing-interest-review__header">
          <div>
            <p className="listing-interest-review__eyebrow">Before you express interest</p>
            <h2 id="listing-interest-review-title">Review this job</h2>
            <p id="listing-interest-review-help">Check both sides of the card and the full posting, especially the clinical requirements.</p>
          </div>
          <button type="button" className="listing-interest-review__close" onClick={onCancel} aria-label="Close job review"><X size={22} /></button>
        </header>
        {!error && <ol className="listing-interest-review__steps" aria-label="Job review progress">
          {steps.map((label, index) => (
            <li key={label} aria-current={index === step ? 'step' : undefined} className={index < step ? 'is-complete' : ''}>
              <span aria-hidden="true">{index < step ? <Check size={14} /> : index + 1}</span>{label}
            </li>
          ))}
        </ol>}
        <div ref={bodyRef} tabIndex={-1} className="listing-interest-review__body" aria-label={steps[step]}>
          {!profile && !error && <p role="status">Loading the complete posting…</p>}
          {error && <div role="alert" className="listing-interest-review__error">
            <h3>{error === 'unavailable' ? 'This job is no longer available' : 'We couldn’t load this job'}</h3>
            <p>{error === 'unavailable'
              ? 'Close this window to keep browsing other opportunities.'
              : 'Please try again to review the complete posting before expressing interest.'}</p>
          </div>}
          {profile && step < 2 && (
            <TooltipContext.Provider value={{ openId, setOpenId }}>
              <div className="listing-interest-review__card">
                {step === 0 ? (
                  external ? <ExternalListingFront listing={profile} sourceType={profile.sourceType} onOpenDetails={expandDetails} />
                    : <ListingFront profile={profile} sourceType={profile.sourceType} hideEmptyRows />
                ) : (
                  external ? <ExternalListingBack listing={profile} onOpenDetails={expandDetails} />
                    : <ListingBack profile={profile} hideEmptyRows onOpenDetails={expandDetails} />
                )}
              </div>
            </TooltipContext.Provider>
          )}
          {profile && step === 2 && (
            <article className="listing-interest-review__posting">
              <h3>{title}</h3>
              <p>{getFullListingDescription(detail)}</p>
              {links.map((link) => <a key={link.key} href={link.url} target="_blank" rel="noopener noreferrer">
                {link.label}<ExternalLink size={16} aria-hidden="true" />
              </a>)}
            </article>
          )}
        </div>
        <footer className="listing-interest-review__footer">
          {profile && step === 2 && <label className="listing-interest-review__acknowledgment">
            <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
            <span>I’ve reviewed the full posting, including the clinical requirements.</span>
          </label>}
          <div className="listing-interest-review__actions">
            <button type="button" className="listing-interest-review__secondary" onClick={step === 0 ? onCancel : () => setStep(step - 1)}>
              {step > 0 && <ArrowLeft size={16} aria-hidden="true" />}{error === 'unavailable' ? 'Close' : step === 0 ? 'Cancel' : 'Back'}
            </button>
            {error === 'load' && <button type="button" className="listing-interest-review__primary" onClick={() => setAttempt((value) => value + 1)}>Try again</button>}
            {!error && <button type="button" className="listing-interest-review__primary"
              disabled={!profile || (step === 2 && !acknowledged)}
              onClick={() => { if (step < 2) expandDetails(); else if (acknowledged) onConfirm(); }}>
              {step === 0 ? 'View back of card' : step === 1 ? 'Expand full job details' : 'Continue with interest'}
              <ArrowRight size={16} aria-hidden="true" />
            </button>}
          </div>
        </footer>
      </section>
    </div>, document.body,
  );
}
